import React, { useEffect, useMemo, useState } from 'react';

import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { Affix, Card, Col, Flex, Row } from 'antd-v5';
import { isEmpty, isNil } from 'lodash';
import { DateTime } from 'luxon';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Heading, Select, Text } from 'scouting-ui/lib';

import type {
  CounselorMeritBadge,
  MeritBadge,
  MeritBadgeCounselorProfile,
  MeritBadgeUserRequirements,
  PostMBCAssignToYouthRequest,
  RosterYouthMember,
} from '@appTypes/esb';
import type { ESBBaseError } from '@appTypes/esb/errors';
import { useMBDict } from '@dict/duck/hooks';
import { routeParamsSel } from '@location/duck/selectorsTyped';
import ErrorBoundary from '@modules/core/components/ErrorBoundary';
import { featureFlags } from '@modules/featureFlags/utils/featureFlags';
import {
  useAssignMBCToYouthMutation,
  useGetMeritBadgeCounselorProfileQuery,
} from '@modules/meritBadgeCounselors/api';
import type { RouteBulkAssignYouth } from '@modules/meritBadgeCounselors/routes.types';
import { useGetUnitRosterYouthsQuery } from '@modules/roster/api';
import { useGetBulkUserMeritBadgeRequirementsQuery } from '@modules/rtk/esb-api';
import { ConditionalRender, Img } from '@shared/components';
import PageContainer from '@shared/components/Layouts/PageContainer';
import { organizationGuidSel } from '@shared/duck/selectors/general.selectors';
import { intl } from '@shared/localization';
import toastService from '@toasts/service';
import { isUnitTypeSel } from '@unit/duck/selectorsTyped';
import { formatToAPIDate } from '@utils/datesLuxon';

import {
  filteredMBSel,
  hasSelectionSel,
  mbSelectionSel,
  selectedYouthSel,
} from '../duck/selectors';
import { bulkAssignActions } from '../duck/slice';
import {
  validateExistingCounselor,
  validateMeritBadgeStatus,
} from '../duck/validations';
import ConfirmationModal, { type ErrorItem } from './ConfirmationModal';
import CounselorInformation from './CounselorInformation';
import { EmptyState, NoAvailableYouth, NoCorrectUnit } from './Messages';
import YouthSelection from './YouthSelection';

interface BulkMBCAssignPageProps {
  mbcProfile: MeritBadgeCounselorProfile;
  mbRequirementsByMid: Map<number, MeritBadgeUserRequirements>;
  availableMeritBadges: Array<{
    counselorMB: CounselorMeritBadge;
    meritBadge: MeritBadge;
  }>;
  youthRoster: RosterYouthMember[];
  isLoading: boolean;
}

type MeritBadgeOption = BulkMBCAssignPageProps['availableMeritBadges'][number];

function MBSelectOption(props: { item: MeritBadgeOption; imgSize: number }) {
  const { imgSize = 32 } = props;
  // this should never happen! (but until SB migrates to akela leave this)
  if (!props.item || !props.item.meritBadge) return <div>Invalid</div>;

  return (
    <Flex align="center" gap="small">
      <Img height={imgSize} src={props.item.meritBadge.imageUrl100} />
      <Text>{props.item.meritBadge.name}</Text>
    </Flex>
  );
}

function BulkMBCAssignPage(props: BulkMBCAssignPageProps) {
  const {
    mbcProfile,
    availableMeritBadges = [],
    isLoading,
    youthRoster: availableYouth = [],
    mbRequirementsByMid,
  } = props;
  const dispatch = useDispatch();
  const [post, { isLoading: isSubmitting }] = useAssignMBCToYouthMutation();
  const selection = useSelector(mbSelectionSel);
  const isUnitType = useSelector(isUnitTypeSel);
  const selectedYouth = useSelector(selectedYouthSel);
  const hasSelection = useSelector(hasSelectionSel);
  const [modalOpen, setModalOpen] = useState(false);
  const [errors, setErrors] = useState<ErrorItem[]>([]);

  const handleSubmit = async function () {
    setErrors([]);
    const today = DateTime.now();
    const selectedMeritBadgeId = selection.counselorMB?.meritBadgeId;

    if (!selectedMeritBadgeId || selectedYouth.length === 0) return;

    const payload: PostMBCAssignToYouthRequest = [
      {
        counselorMemberId: mbcProfile.memberId,
        youths: selectedYouth.map(youth => ({
          effectiveDt: formatToAPIDate(today),
          youthMemberId: youth.memberId,
          youthLastName: youth.lastName,
          meritBadgesCounseling: [{ meritBadgeId: selectedMeritBadgeId }],
        })),
      },
    ];
    try {
      toastService.dismiss(undefined);
      await post({
        payload,
      }).unwrap();
      toastService.success(
        intl.formatMessage({ id: 'page.mbcBulkAssign.alerts.success' }),
      );
      dispatch(bulkAssignActions.resetAfterDone());
      setModalOpen(false);
    } catch (err) {
      const error = err as FetchBaseQueryError;
      const errorData = error.data as Array<{
        youthMemberId: number;
        processed: boolean;
        message: string;
      }>;
      if (Array.isArray(errorData) && errorData.length) {
        const failedItems = errorData
          .filter(item => !item.processed)
          .map(({ youthMemberId, message }) => ({ youthMemberId, message }));
        setErrors(failedItems);
      } else {
        const data = error.data as ESBBaseError[];
        toastService.error(
          data?.at(0)?.errorDesc ||
            intl.formatMessage({ id: 'shared.errors.generic' }),
        );
      }
    }
  };

  const handleCloseModal = () => {
    setErrors([]);
    setModalOpen(false);
  };

  const selectOptions = availableMeritBadges.map(item => ({
    ...item,
    value: item.counselorMB.meritBadgeId,
    label: item.meritBadge?.name,
    title: item.meritBadge?.name,
  }));
  type SelectOption = (typeof selectOptions)[number];

  if (!mbcProfile) return null;

  const noSelectionMade = !hasSelection;

  const disableSubmit =
    isSubmitting || noSelectionMade || isEmpty(selectedYouth);

  const valid = !noSelectionMade && isUnitType;

  return (
    <main>
      <ConditionalRender value={hasSelection}>
        {modalOpen && (
          <ConfirmationModal
            isLoading={isSubmitting}
            open={modalOpen}
            mbcProfle={mbcProfile}
            counselorMB={selection.counselorMB!}
            selectedYouth={selectedYouth}
            onSubmit={handleSubmit}
            meritBadge={selection.mb!}
            errors={errors}
            onCancel={handleCloseModal}
            onClearErrors={() => setErrors([])}
          />
        )}
      </ConditionalRender>
      <Flex vertical gap="middle">
        <Row>
          <Col xs={24}>
            <Heading font="display-h4" level={1}>
              <FormattedMessage id="page.mbcBulkAssign.page.title" />
            </Heading>
          </Col>
        </Row>
        <Affix>
          <Card>
            <Row gutter={[16, 16]} align={'middle'}>
              <Col xs={24} md={{ flex: 'auto' }}>
                <Select
                  listHeight={500}
                  virtual={false}
                  onChange={(_item, option: SelectOption) =>
                    dispatch(
                      bulkAssignActions.setSelection({
                        counselorMB: option.counselorMB,
                        mb: option.meritBadge,
                      }),
                    )
                  }
                  value={selection.counselorMB?.meritBadgeId}
                  placeholder={
                    <FormattedMessage id="page.mbcBulkAssign.selectMB" />
                  }
                  labelRender={item => {
                    const mbID = item.value;
                    const option = selectOptions.find(
                      item => item.counselorMB.meritBadgeId === mbID,
                    );
                    if (!option) return 'Invalid';
                    return <MBSelectOption imgSize={22} item={option} />;
                  }}
                  optionRender={item => {
                    const data = item.data as (typeof selectOptions)[number];

                    return <MBSelectOption item={data} imgSize={32} />;
                  }}
                  loading={isLoading}
                  showSearch
                  optionFilterProp="title"
                  options={selectOptions}
                />
              </Col>
              <Col xs={24} md={{ flex: 'auto' }}>
                <Flex justify="flex-end" align="center">
                  <Button
                    block
                    onClick={() => setModalOpen(true)}
                    disabled={disableSubmit}
                  >
                    <FormattedMessage id="shared.submit" />
                  </Button>
                </Flex>
              </Col>
            </Row>
          </Card>
        </Affix>
        <ConditionalRender value={!valid}>
          {isUnitType && noSelectionMade ? (
            <EmptyState />
          ) : !isUnitType ? (
            <NoCorrectUnit />
          ) : null}
        </ConditionalRender>
        <ConditionalRender value={valid}>
          <Row gutter={[16, 16]}>
            <Col xs={{ order: 1, span: 24 }} sm={{ span: 10, order: 0 }}>
              <YouthSelection
                youths={availableYouth}
                mbRequirementsByMid={mbRequirementsByMid}
              />
            </Col>

            <Col xs={{ span: 24, order: 0 }} sm={{ span: 14, order: 1 }}>
              <ConditionalRender value={props.youthRoster.length === 0}>
                <NoAvailableYouth />
              </ConditionalRender>
              <ConditionalRender value={props.youthRoster.length > 0}>
                <Card>
                  {!!hasSelection && (
                    <CounselorInformation
                      meritBadge={selection.mb!}
                      mbcProfile={mbcProfile}
                      counselorMB={selection.counselorMB!}
                    />
                  )}
                </Card>
              </ConditionalRender>
            </Col>
          </Row>
        </ConditionalRender>
      </Flex>
    </main>
  );
}

function BulkMBCAssignPageContainer() {
  const organizationGuid = useSelector(organizationGuidSel);
  const { counselorMemberId: memberId } = useSelector(
    routeParamsSel<RouteBulkAssignYouth>,
  );
  const {
    meritBadgesById,
    response: { isLoading: isLoadingMBDict },
  } = useMBDict({ params: { status: 'all' } });
  const selection = useSelector(mbSelectionSel);
  const hasSelection = useSelector(hasSelectionSel);
  const {
    isFetching: isLoadingRosterYouths,
    isLoading,
    data: youthRoster,
  } = useGetUnitRosterYouthsQuery(
    {
      organizationGuid,
    },
    {
      skip: !organizationGuid,
    },
  );
  const dispatch = useDispatch();

  const {
    data: mbcProfile,
    isLoading: isLoadingMbcProfile,
    isUninitialized,
  } = useGetMeritBadgeCounselorProfileQuery(
    {
      memberId,
    },
    { skip: isNil(memberId) },
  );
  const youthMIDs = Array.from(
    new Set(youthRoster?.users.map(youth => youth.memberId)),
  );
  const { data: userMbRequirements } =
    useGetBulkUserMeritBadgeRequirementsQuery(
      {
        meritBadgeId: Number(selection.counselorMB?.meritBadgeId),
        orgGuid: organizationGuid,
        params: {
          memberId: youthMIDs,
        },
      },
      { skip: youthMIDs.length === 0 || !organizationGuid || !hasSelection },
    );

  useEffect(() => {
    dispatch(bulkAssignActions.setMBCProfile({ mbcProfile: mbcProfile }));
  }, [dispatch, mbcProfile]);

  const filteredMBs = useSelector(filteredMBSel);
  const users = youthRoster?.users;

  const mbRequirementsByMid = useMemo(() => {
    const requirementsByMID = new Map(
      userMbRequirements?.map(item => [item.memberId, item]) || [],
    );
    return requirementsByMID;
  }, [userMbRequirements]);

  useEffect(() => {
    const validations = (users || [])
      .map(youth => {
        const validations = [
          validateExistingCounselor(youth, selection.counselorMB!, mbcProfile!),
          validateMeritBadgeStatus({
            requirementsByMID: mbRequirementsByMid,
            youth,
          }),
        ];
        const errors = validations.filter(res => res.valid === false);
        if (errors.length === 0) return null;

        return { youth, errors };
      })
      .filter(item => item !== null);

    const validationsByMid = Object.fromEntries(
      validations.map(item => [item.youth.memberId, item.errors]),
    );

    dispatch(
      bulkAssignActions.setValidationResults({
        validationErrors: validationsByMid,
      }),
    );
  }, [
    dispatch,
    mbcProfile,
    mbcProfile?.meritBadges,
    selection.counselorMB,
    mbRequirementsByMid,
    users,
  ]);

  useEffect(
    () => () => {
      dispatch(bulkAssignActions.resetState());
    },
    [dispatch],
  );

  if (!mbcProfile) return null;

  if (!mbcProfile && !isUninitialized)
    return (
      <div>
        <FormattedMessage id="page.mbcBulkAssign.invalidProfile" />
      </div>
    );

  const availableMeritBadges = filteredMBs
    .map(item => ({
      counselorMB: item,
      meritBadge: meritBadgesById.get(item.meritBadgeId)!,
    }))
    .filter(item => !!item.meritBadge);

  return (
    <BulkMBCAssignPage
      youthRoster={youthRoster?.users || []}
      mbRequirementsByMid={mbRequirementsByMid}
      availableMeritBadges={availableMeritBadges}
      mbcProfile={mbcProfile}
      isLoading={
        isLoading ||
        isLoadingMBDict ||
        isLoadingMbcProfile ||
        isLoadingRosterYouths
      }
    />
  );
}

export default function BulkMBCAssignPageWrapper() {
  if (!featureFlags.getFlag('SBL_4357_BULK_ASSIGN_MBC')) return null;

  return (
    <ErrorBoundary>
      <PageContainer>
        <BulkMBCAssignPageContainer />
      </PageContainer>
    </ErrorBoundary>
  );
}
